import { useDepartments } from '@hooks/useDepartments'
import { useFacilities } from '@hooks/useFacilities'
import { Button } from 'components/buttons'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Picker } from './Picker'

interface ApproverModalContentProps {
  userSection: ReactNode
  selectedFacilityIds: Set<number>
  selectedDepartmentIds: Set<string>
  allFacilities: boolean
  allDepartments: boolean
  onFacilitySelect: (facility: { id: number }, selected: boolean) => void
  onDepartmentSelect: (department: { id: string }, selected: boolean) => void
  onAllFacilitiesToggle: (selected: boolean) => void
  onAllDepartmentsToggle: (selected: boolean) => void
  onCancel: () => void
  onSave: () => void
  isLoading: boolean
  saveButtonText: string
  disableSaveButton?: boolean
}

export const ApproverModalContent = ({
  userSection,
  selectedFacilityIds,
  selectedDepartmentIds,
  allFacilities,
  allDepartments,
  onFacilitySelect,
  onDepartmentSelect,
  onAllFacilitiesToggle,
  onAllDepartmentsToggle,
  onCancel,
  onSave,
  isLoading,
  saveButtonText,
  disableSaveButton,
}: ApproverModalContentProps) => {
  const { t } = useTranslation()
  return (
    <>
      <div className="flex flex-col border-y border-neutral-200 p-4 min-w-[1024px] h-[450px]">
        <div className="flex flex-row gap-4 grow max-h-full p-2">
          <div className="flex-1 flex flex-col gap-2">
            <span className="text-md font-semibold">{t('features.approvalDomainApprovers.user')}</span>
            {userSection}
          </div>
          <div className="flex-1 flex flex-col gap-2">
            <span className="text-md font-semibold">{t('features.approvalDomainApprovers.selectFacilities')}</span>
            <Picker
              allSelected={allFacilities}
              filter={(facility, search) =>
                facility.name.toLowerCase().includes(search.toLowerCase())
              }
              idAccessor={(facility) => facility.id}
              label={(facility) => facility.name}
              onAllSelect={(selected) => {
                onAllFacilitiesToggle(selected)
              }}
              onSelect={onFacilitySelect}
              placeholder={t('features.approvalDomainApprovers.searchByFacility')}
              selectedIds={selectedFacilityIds}
              useQuery={useFacilities}
            />
          </div>
          <div className="flex-1 flex flex-col gap-2">
            <span className="text-md font-semibold">{t('features.approvalDomainApprovers.selectDepartments')}</span>
            <Picker
              allSelected={allDepartments}
              filter={(department, search) =>
                department.name.toLowerCase().includes(search.toLowerCase())
              }
              idAccessor={(department) => department.id}
              label={(department) => department.name}
              onAllSelect={(selected) => {
                onAllDepartmentsToggle(selected)
              }}
              onSelect={onDepartmentSelect}
              placeholder={t('features.approvalDomainApprovers.searchByDepartment')}
              selectedIds={selectedDepartmentIds}
              useQuery={useDepartments}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-row justify-between items-center p-4">
        <Button variant="outlined" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={onSave}
          disabled={isLoading || disableSaveButton}
          loading={isLoading}
        >
          {saveButtonText}
        </Button>
      </div>
    </>
  )
}
